/**
 * Platform owner/super-admin console (Task #25). Lists every partner firm and lets the platform
 * owner APPROVE (activate) or SUSPEND them. Suspension is enforced in the database (migration
 * 0009) — a suspended firm's staff lose all access, this screen just drives it.
 *
 * This screen shows firm METADATA only (name, licence, owner email, member count) — never any
 * client's data. Access is gated server-side (platform admin + authenticator); this component is
 * only ever mounted once /v1/admin/whoami confirms the caller is an admin.
 */
import React, { useEffect, useState } from 'react';
import { ShieldCheck, Building2, RefreshCw, CheckCircle2, Ban, Loader2, Gift, Mail, X, FileCode2, ClipboardCheck } from 'lucide-react';
import UsagePanel from './UsagePanel.tsx';
import InsightsPanel from './InsightsPanel.tsx';
import RegulatoryPanel from './RegulatoryPanel.tsx';
import { AdminShell, AdminMessage, HeaderButton, SectionLabel, StatusDot } from './AdminShell.tsx';
import {
  listAdminTenants,
  setTenantStatus,
  grantFreeAccess,
  removeFreeAccess,
  preauthoriseFreeAccessByEmail,
  listFreeAccessEmails,
  removeFreeAccessByEmail,
  explainAdminError,
} from '../../services/adminApi.ts';
import type { AdminTenant, FreeAccessEmail } from '../../services/adminApi.ts';
import { PROFESSION_LABELS, type Profession } from '../../services/tenantApi.ts';
import { confirmAction } from '../../components/ui/notices.tsx';

const TABS = [
  { key: 'firms', label: 'Firms' },
  { key: 'usage', label: 'Usage' },
  { key: 'insights', label: 'Client insights' },
  { key: 'regulatory', label: 'Regulatory prompts' },
] as const;

/** Status as a colour: the dot carries the meaning, the word says it. */
const statusColor = (status: string): string =>
  status === 'active' ? '#059669' : status === 'suspended' ? '#dc2626' : '#d97706';

/** Plain-language access for a firm row. "Free access" (comp), "Paid", or "No plan". */
const accessBadge = (t: AdminTenant): { label: string; color: string } => {
  if (t.freeAccess) return { label: 'Free access', color: '#7c3aed' };
  if (t.hasAccess) return { label: 'Paid', color: '#059669' };
  return { label: 'No plan', color: '#94a3b8' };
};

const quietButton =
  'inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:text-slate-900 disabled:opacity-50';

interface Notice {
  tone: 'success' | 'error';
  text: string;
}

interface AdminConsoleProps {
  onBack: () => void;
  /** "Sign out" when the console is the admin's home (they have no firm); otherwise "Back". */
  backLabel?: string;
  onOpenApiDocs?: () => void;
  /** Present when the signed-in user may review advice given under our licence. */
  onOpenCompliance?: () => void;
}

const AdminConsole = ({ onBack, backLabel, onOpenApiDocs, onOpenCompliance }: AdminConsoleProps) => {
  // Firms (manage), Usage (counts across the platform), Client insights, or Regulatory prompts. All
  // client-data-free.
  const [tab, setTab] = useState<'firms' | 'usage' | 'insights' | 'regulatory'>('firms');
  const [tenants, setTenants] = useState<AdminTenant[] | null>(null); // null = loading
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [freeBusyId, setFreeBusyId] = useState<string | null>(null);
  const [consumerEmail, setConsumerEmail] = useState('');
  const [consumerBusy, setConsumerBusy] = useState(false);
  const [freeEmails, setFreeEmails] = useState<FreeAccessEmail[]>([]);
  const [emailBusy, setEmailBusy] = useState<string | null>(null); // email currently being removed

  const load = async () => {
    setError(null);
    try {
      setTenants(await listAdminTenants());
    } catch (err) {
      setTenants([]);
      setError(explainAdminError(err, 'Could not load firms just now.'));
    }
    try {
      setFreeEmails(await listFreeAccessEmails());
    } catch {
      /* the allowlist is a secondary panel — don't block the firms view on it */
    }
  };

  useEffect(() => {
    load();
  }, []);

  const changeStatus = async (tenant: AdminTenant, status: 'active' | 'suspended') => {
    const verb = status === 'suspended' ? 'suspend' : 'reactivate';
    if (status === 'suspended') {
      const sure = await confirmAction(
        `Suspend ${tenant.name}? Everyone at the firm immediately loses access to their client files until you reactivate it. Client data is preserved.`,
      );
      if (!sure) return;
    }
    setBusyId(tenant.id);
    setNotice(null);
    try {
      await setTenantStatus(tenant.id, status);
      setTenants((prev) => (prev ? prev.map((t) => (t.id === tenant.id ? { ...t, status } : t)) : prev));
      setNotice({ tone: 'success', text: `${tenant.name} ${status === 'suspended' ? 'suspended' : 'reactivated'}.` });
    } catch (err) {
      setNotice({ tone: 'error', text: explainAdminError(err, `Could not ${verb} the firm.`) });
    } finally {
      setBusyId(null);
    }
  };

  const toggleFreeAccess = async (tenant: AdminTenant) => {
    const turningOn = !tenant.freeAccess;
    if (!turningOn) {
      const sure = await confirmAction(
        `Remove free access from ${tenant.name}? They'll be locked out until they subscribe or you grant it again. This never touches a paid subscription.`,
      );
      if (!sure) return;
    }
    setFreeBusyId(tenant.id);
    setNotice(null);
    try {
      if (turningOn) await grantFreeAccess(tenant.id);
      else await removeFreeAccess(tenant.id);
      setTenants((prev) =>
        prev
          ? prev.map((t) =>
              t.id === tenant.id ? { ...t, freeAccess: turningOn, hasAccess: turningOn ? true : t.hasAccess && t.plan !== 'comp' ? t.hasAccess : false } : t,
            )
          : prev,
      );
      setNotice({ tone: 'success', text: `${turningOn ? 'Granted' : 'Removed'} free access ${turningOn ? 'to' : 'from'} ${tenant.name}.` });
    } catch (err) {
      setNotice({ tone: 'error', text: explainAdminError(err, `Could not ${turningOn ? 'grant' : 'remove'} free access.`) });
    } finally {
      setFreeBusyId(null);
    }
  };

  const grantConsumer = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const email = consumerEmail.trim();
    if (!email) return;
    setConsumerBusy(true);
    setNotice(null);
    try {
      const res = await preauthoriseFreeAccessByEmail(email);
      setNotice({
        tone: 'success',
        text: res?.applied
          ? `Free access is now active for ${res.tenantName || email}.`
          : `${email} is on the free-access list — they won't be charged when they sign up.`,
      });
      setConsumerEmail('');
      try { setFreeEmails(await listFreeAccessEmails()); } catch { /* keep going */ }
    } catch (err) {
      setNotice({ tone: 'error', text: explainAdminError(err, 'Could not add that email to free access.') });
    } finally {
      setConsumerBusy(false);
    }
  };

  const removeEmail = async (email: string) => {
    if (!await confirmAction(`Remove free access for ${email}? If they already have an account, their free access is turned off too.`)) return;
    setEmailBusy(email);
    setNotice(null);
    try {
      await removeFreeAccessByEmail(email);
      setFreeEmails((prev) => prev.filter((r) => r.email !== email));
      setNotice({ tone: 'success', text: `Removed free access for ${email}.` });
    } catch (err) {
      setNotice({ tone: 'error', text: explainAdminError(err, 'Could not remove that email.') });
    } finally {
      setEmailBusy(null);
    }
  };

  const pendingCount = (tenants || []).filter((t) => t.status !== 'active').length;

  const subtitle =
    tenants === null
      ? 'Loading…'
      : `${tenants.length} firm${tenants.length === 1 ? '' : 's'}${pendingCount > 0 ? ` · ${pendingCount} not active` : ''}`;

  return (
    <AdminShell
      title="Platform admin"
      subtitle={subtitle}
      icon={<ShieldCheck size={22} />}
      onBack={onBack}
      backLabel={backLabel}
      tabs={TABS}
      activeTab={tab}
      onTab={setTab}
      actions={
        <>
          {onOpenCompliance && (
            <HeaderButton onClick={onOpenCompliance}>
              <ClipboardCheck size={15} /> Advice under our licence
            </HeaderButton>
          )}
          {onOpenApiDocs && (
            <HeaderButton onClick={onOpenApiDocs}>
              <FileCode2 size={15} /> API docs
            </HeaderButton>
          )}
          <HeaderButton onClick={load}>
            <RefreshCw size={15} /> Refresh
          </HeaderButton>
        </>
      }
    >
      {tab === 'usage' ? (
        <UsagePanel />
      ) : tab === 'insights' ? (
        <InsightsPanel />
      ) : tab === 'regulatory' ? (
        <RegulatoryPanel />
      ) : (
        <>
          {notice && <AdminMessage tone={notice.tone}>{notice.text}</AdminMessage>}
          {error && <AdminMessage tone="error">{error}</AdminMessage>}

          <SectionLabel>Partner firms</SectionLabel>
          {tenants === null ? (
            <div className="flex items-center justify-center gap-2 py-20 text-slate-400">
              <Loader2 size={20} className="animate-spin" /> Loading firms…
            </div>
          ) : tenants.length === 0 && !error ? (
            <div className="rounded-xl border border-dashed border-slate-300 py-16 text-center text-slate-500">
              <Building2 size={28} className="mx-auto mb-3 text-slate-300" />
              No partner firms yet.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-[11px] uppercase tracking-[0.08em] text-slate-500">
                    <th className="px-5 py-3 font-semibold">Firm</th>
                    <th className="px-4 py-3 font-semibold">Licence</th>
                    <th className="px-4 py-3 font-semibold">Owner</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Access</th>
                    <th className="px-5 py-3 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {tenants.map((t) => {
                    const access = accessBadge(t);
                    return (
                      <tr key={t.id} className="align-middle transition hover:bg-slate-50/70">
                        <td className="px-5 py-4">
                          <div className="font-semibold text-slate-900">{t.name}</div>
                          <div className="mt-0.5 text-xs text-slate-500">
                            {t.profession ? (PROFESSION_LABELS[t.profession as Profession] ?? t.profession) : '—'}
                            {' · '}{t.memberCount} member{t.memberCount === 1 ? '' : 's'}
                            {t.createdAt ? ` · joined ${new Date(t.createdAt).toLocaleDateString('en-AU')}` : ''}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-slate-700">
                          {t.afslLicenseeName ? (
                            <>
                              {t.afslLicenseeName}
                              {t.afslNumber ? <div className="text-xs tabular-nums text-slate-500">AFSL {t.afslNumber}</div> : null}
                            </>
                          ) : (
                            <span className="text-slate-500">Under our licence</span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-slate-700">{t.ownerEmail || <span className="text-slate-400">—</span>}</td>
                        <td className="px-4 py-4">
                          <StatusDot color={statusColor(t.status)} label={t.status.charAt(0).toUpperCase() + t.status.slice(1)} />
                        </td>
                        <td className="px-4 py-4">
                          <StatusDot color={access.color} label={access.label} />
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex justify-end gap-2">
                            <button type="button" disabled={freeBusyId === t.id} onClick={() => toggleFreeAccess(t)} className={quietButton}>
                              {freeBusyId === t.id ? <Loader2 size={14} className="animate-spin" /> : <Gift size={14} className={t.freeAccess ? '' : 'text-violet-600'} />}
                              {t.freeAccess ? 'Remove free access' : 'Free access'}
                            </button>
                            {t.status === 'suspended' ? (
                              <button type="button" disabled={busyId === t.id} onClick={() => changeStatus(t, 'active')}
                                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-50">
                                {busyId === t.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Reactivate
                              </button>
                            ) : (
                              <button type="button" disabled={busyId === t.id} onClick={() => changeStatus(t, 'suspended')}
                                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:border-red-300 hover:text-red-700 disabled:opacity-50">
                                {busyId === t.id ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />} Suspend
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <section className="mt-10 grid gap-6 border-t border-slate-200 pt-8 lg:grid-cols-[1fr_1.1fr]">
            <div>
              <SectionLabel>Free access for a person</SectionLabel>
              <p className="max-w-prose text-sm leading-relaxed text-slate-600">
                For a direct customer, not a firm. Enter the email they sign in with and they won’t be charged when they sign up or
                sign in. It works before or after they create an account, and you can remove it at any time.
              </p>
            </div>
            <div>
              <form onSubmit={grantConsumer} className="flex flex-col gap-2 sm:flex-row">
                <label className="relative flex-1">
                  <span className="sr-only">Email address</span>
                  <Mail size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    value={consumerEmail}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConsumerEmail(e.target.value)}
                    placeholder="person@example.com"
                    className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-9 pr-3 text-sm text-slate-800 outline-none transition focus:border-[#1a6fd4] focus:ring-2 focus:ring-[#1a6fd4]/20"
                  />
                </label>
                <button
                  type="submit"
                  disabled={consumerBusy || !consumerEmail.trim()}
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-700 disabled:bg-slate-300 disabled:text-white"
                >
                  {consumerBusy ? <Loader2 size={15} className="animate-spin" /> : <Gift size={15} />} Give free access
                </button>
              </form>

              {freeEmails.length > 0 && (
                <div className="mt-5">
                  <div className="mb-2 text-xs text-slate-500">
                    {freeEmails.length} {freeEmails.length === 1 ? 'person has' : 'people have'} free access
                  </div>
                  <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200">
                    {freeEmails.map((r) => (
                      <li key={r.email} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                        <span className="truncate text-slate-700">{r.email}</span>
                        <button
                          type="button"
                          onClick={() => removeEmail(r.email)}
                          disabled={emailBusy === r.email}
                          className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-slate-500 transition hover:text-red-600 disabled:opacity-50"
                        >
                          {emailBusy === r.email ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />} Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>

          <p className="mt-10 text-center text-xs text-slate-400">
            Actions here are recorded on the tamper-evident audit trail. This screen never shows client data.
          </p>
        </>
      )}
    </AdminShell>
  );
};

export default AdminConsole;
